import React, { useMemo } from 'react';

import { Box, useTheme } from '@material-ui/core';

import { GridCellWithAvatar, AppIconButton, AppLink } from '@vyce/core/src/components';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import { WidgetWrapper, WidgetWrapperProps } from '@vyce/core/src/components/Dashboard/WidgetWrapper';
import { RiDownloadCloud2Line } from 'react-icons/ri';

import { useWidgetData } from './hooks';
import { EnrichedColumnProps } from '../../../components/InfiniteTable/types';
import { InfiniteTable } from '../../../components/InfiniteTable';
import { RecentlyRegisteredDTO } from '../../../types';
import { formatDate } from '../../../utils';
import type { Props } from './types';
import useChatRequests from '../../../hooks/useChatRequests';

/* Recently Registered Widget */

export const RecentlyRegisteredWidget = (props: Props & WidgetWrapperProps) => {
  const {
    workers,
    isLoadingList,
    total,
    downloadButtonLoading,
    loading,
    getRecentlyRegisteredWorkers,
    resetLoadingList,
    getSharedFile,
    getPath,
  } = useWidgetData(props);
  const theme = useTheme();

  const { createPrivateChat } = useChatRequests();

  const goToChat = async (worker: RecentlyRegisteredDTO) => {
    if (!props.chatUser || !props.selectedCompanyId) {
      return;
    }
    await createPrivateChat({
      creator: props.chatUser,
      chatUser: {
        uid: worker.user_id,
        name: `${worker.first_name} ${worker.last_name}`,
        email: worker.email,
        phone: worker.phone,
        avatarUrl: worker.avatar,
      },
      companyId: props.selectedCompanyId,
    });
  };

  const columns: EnrichedColumnProps<RecentlyRegisteredDTO>[] = useMemo(
    () => [
      {
        field: 'first_name',
        headerName: 'Name',
        renderCell: row => (
          <GridCellWithAvatar
            createPrivateChat={() => goToChat(row)}
            avatarUrl={row.avatar}
            avatarPlaceholder={getAvatar(row.gender)}
            link={getPath({ userId: row.user_id, firstName: row.first_name })}
            name={`${row.first_name} ${row.last_name}`}
            target="_blank"
          />
        ),
        flex: 0.5,
        width: 100,
        sortable: true,
      },
      {
        field: 'ni_number',
        headerName: 'Ref #',
        flex: 0.5,
        width: 150,
        sortable: true,
      },
      {
        field: 'email',
        headerName: 'Email',
        renderCell: row => (
          <AppLink target="_black" to={`mailto: ${row.email}`}>
            {row.email}
          </AppLink>
        ),
        flex: 0.5,
        width: 100,
        sortable: true,
      },
      {
        field: 'phone',
        headerName: 'Phone Number',
        renderCell: row => (
          <AppLink target="_black" to={`tel: ${row.phone}`}>
            {row.phone}
          </AppLink>
        ),
        flex: 0.5,
        width: 100,
        sortable: true,
      },
      {
        field: 'registered_at',
        headerName: 'Registration Date',
        flex: 0.9,
        width: 220,
        sortable: false,
        valueGetter: row => formatDate(row.registered_at, 'DD/MM/YYYY'),
      },
    ],
    []
  );

  return (
    <WidgetWrapper
      {...props}
      renderExtraControls={() => (
        <>
          {props.getRecentlyRegisteredSharedData ? (
            <AppIconButton
              variant="paper"
              isSmall
              isBorder
              hidden={!props.getRecentlyRegisteredSharedData}
              onClick={getSharedFile}
              disabled={downloadButtonLoading}
            >
              <RiDownloadCloud2Line size="20px" color={theme.palette.text.primary} />
            </AppIconButton>
          ) : null}
        </>
      )}
    >
      <Box height="100%" width="100%">
        <InfiniteTable
          isLoading={loading}
          data={workers}
          columns={columns}
          rowCount={total}
          rowHeight={54}
          height="calc(100% - 1px)"
          pageSize={GRID_PAGE_SIZE}
          isLoadingList={isLoadingList}
          onLoadMore={getRecentlyRegisteredWorkers}
          resetLoadingList={resetLoadingList}
        />
      </Box>
    </WidgetWrapper>
  );
};
